import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import posthog from "posthog-js";
import { PostHogProvider } from "@posthog/react";
import { useCookieConsent } from "../context/CookieConsentContext";
import { getPostHogConfig, initPostHogIfNeeded, mapUserRoleForAnalytics } from "../lib/posthogConfig";

function PostHogPageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    if (!getPostHogConfig().enabled) return undefined;
    if (!initPostHogIfNeeded()) return undefined;
    posthog.capture("$pageview", {
      $current_url: window.location.href,
    });
    return undefined;
  }, [location.pathname, location.search, location.hash]);

  return null;
}

function PostHogIdentifyUser({ currentUser }) {
  const lastIdentifiedKeyRef = useRef("");

  useEffect(() => {
    if (!getPostHogConfig().enabled) return undefined;
    if (!initPostHogIfNeeded()) return undefined;

    const userId = currentUser?.id != null ? String(currentUser.id).trim() : "";
    if (!userId) {
      if (lastIdentifiedKeyRef.current) {
        posthog.reset();
        lastIdentifiedKeyRef.current = "";
      }
      return undefined;
    }

    const role = mapUserRoleForAnalytics(currentUser?.role);
    const identifyKey = `${userId}|${role || ""}`;
    if (identifyKey === lastIdentifiedKeyRef.current) return undefined;

    posthog.identify(userId, role ? { role } : undefined);
    lastIdentifiedKeyRef.current = identifyKey;
    return undefined;
  }, [currentUser]);

  return null;
}

/**
 * PostHog analytics (public app only). Loads when REACT_APP_POSTHOG_KEY is set and the user
 * has accepted analytics cookies — not on admin host.
 */
export default function PostHogAnalytics({ currentUser, children }) {
  const { consent } = useCookieConsent();
  const { enabled } = getPostHogConfig();
  const analyticsAllowed = enabled && Boolean(consent?.analytics);

  useEffect(() => {
    if (!analyticsAllowed) return undefined;
    initPostHogIfNeeded();
    return undefined;
  }, [analyticsAllowed]);

  if (!analyticsAllowed) return children;

  return (
    <PostHogProvider client={posthog}>
      <PostHogPageViewTracker />
      <PostHogIdentifyUser currentUser={currentUser} />
      {children}
    </PostHogProvider>
  );
}
